/**
 * Pickup Location Form Modal
 * Unified modal for creating and editing pickup locations
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUIStore } from '@/stores/uiStore';
import {
  createPickupLocation,
  updatePickupLocation,
} from '@/lib/api/pickupLocations';
import {
  DEFAULT_LOCATION_COLOR,
  HEX_COLOR_RE,
  PRESET_LOCATION_COLORS,
  normalizeLocationColor,
} from '@/lib/utils/locationColor';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import type { PickupLocation } from '@/types/api';

const locationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  address: z.string().max(1000).optional(),
  colorCode: z.string().regex(HEX_COLOR_RE, 'Color code must be a valid hex color'),
  isActive: z.boolean(),
});

type LocationForm = z.infer<typeof locationSchema>;

interface PickupLocationModalProps {
  mode: 'create' | 'edit';
  location?: PickupLocation; // Required for edit mode
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function PickupLocationModal({
  mode,
  location,
  open,
  onOpenChange,
  onSuccess,
}: PickupLocationModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useUIStore();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<LocationForm>({
    defaultValues: {
      name: location?.name || '',
      address: location?.address || '',
      colorCode: location?.colorCode || DEFAULT_LOCATION_COLOR,
      isActive: location?.isActive ?? true,
    },
  });

  const selectedColorCode = normalizeLocationColor(watch('colorCode'));

  // Reset form when location changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && location) {
      reset({
        name: location.name,
        address: location.address || '',
        colorCode: location.colorCode,
        isActive: location.isActive,
      });
    } else if (mode === 'create') {
      reset({
        name: '',
        address: '',
        colorCode: DEFAULT_LOCATION_COLOR,
        isActive: true,
      });
    }
  }, [mode, location, reset, open]);

  const onSubmit = async (data: LocationForm) => {
    try {
      setSubmitting(true);

      if (mode === 'create') {
        await createPickupLocation({
          name: data.name,
          address: data.address || null,
          colorCode: normalizeLocationColor(data.colorCode),
          isActive: data.isActive,
        });
        showToast('Pickup location created successfully', 'success');
      } else if (mode === 'edit' && location) {
        await updatePickupLocation(location.id, {
          name: data.name,
          address: data.address || null,
          colorCode: normalizeLocationColor(data.colorCode),
          isActive: data.isActive,
        });
        showToast('Pickup location updated successfully', 'success');
      }

      reset();
      onSuccess();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : `Failed to ${mode} pickup location`,
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'create' ? 'Create Pickup Location' : 'Edit Pickup Location';
  const description = mode === 'create'
    ? 'Add a new pickup location for meal distribution'
    : 'Update pickup location information';
  const submitText = mode === 'create' ? 'Create Location' : 'Save Changes';
  const submittingText = mode === 'create' ? 'Creating...' : 'Saving...';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Location Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g., Main Mosque"
              disabled={submitting}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              {...register('address')}
              placeholder="Full address (optional)"
              disabled={submitting}
              rows={3}
            />
            {errors.address && (
              <p className="text-sm text-red-500">{errors.address.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="colorCodePicker">
              Location Color <span className="text-red-500">*</span>
            </Label>
            <input type="hidden" {...register('colorCode')} />
            <div className="flex items-center gap-3">
              <Input
                id="colorCodePicker"
                type="color"
                value={selectedColorCode}
                onChange={(e) => setValue('colorCode', e.target.value.toUpperCase())}
                disabled={submitting}
                className="h-10 w-14 p-1 cursor-pointer"
              />
              <span className="text-sm font-mono text-muted-foreground">
                {selectedColorCode}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_LOCATION_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Select ${color}`}
                  className="h-7 w-7 rounded-md border cursor-pointer"
                  style={{ backgroundColor: color }}
                  onClick={() => setValue('colorCode', color)}
                  disabled={submitting}
                />
              ))}
            </div>
            <div className="rounded-md border px-3 py-2 bg-muted/30">
              <p className="text-xs text-muted-foreground mb-2">Preview</p>
              <LocationColorBadge colorCode={selectedColorCode} className="rounded-full px-2.5 py-1 text-xs font-semibold">
                {location?.name || 'Location'}
              </LocationColorBadge>
              <span className="ml-2 text-xs font-mono text-muted-foreground">
                {selectedColorCode}
              </span>
            </div>
            {errors.colorCode && (
              <p className="text-sm text-red-500">{errors.colorCode.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              disabled={submitting}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="isActive" className="font-normal cursor-pointer">
              Active (members can select this location during registration)
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? submittingText : submitText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Convenience exports for clearer usage
export function CreatePickupLocationModal(props: Omit<PickupLocationModalProps, 'mode' | 'location'>) {
  return <PickupLocationModal mode="create" {...props} />;
}

export function EditPickupLocationModal(props: Omit<PickupLocationModalProps, 'mode'> & { location: PickupLocation }) {
  return <PickupLocationModal mode="edit" {...props} />;
}
